import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  Button, 
  Skeleton,
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Stack,
  ToggleButton, 
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';


// Import icons from @mui/icons-material instead of @mui/material
import {
  TextFields,
  Subject,
  RadioButtonChecked,
  CheckBox,
  Email,
  Numbers,
  CalendarToday as CalendarMonth,
  Upload,
  Star,
  Phone,
  TableChart,
  LinearScale,
  Sort,
  AccessTime,
  EventNote,
  Language,
  LocationOn,
  Looks3,
  CompareArrows,
  PeopleOutline,
  Download,
  ExpandMore as ExpandMoreIcon,
  BarChart, 
  PieChart, 
  ShowChart,
  ViewList,
  Image as ImageIcon,
} from '@mui/icons-material';

import { getApiUrl } from '../../config/api';
import API_CONFIG from '../../config/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import { fetchSurveyResponses } from '../../store/surveyResponsesSlice';

// Use logo from public folder to avoid build path issues
const logo = process.env.PUBLIC_URL + '/logo.png';

// Add this comprehensive HTML entity decoder function at the top of your file
const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  const entities = {
    // Basic entities
    '&nbsp;': ' ', 
    '&lt;': '<', 
    '&gt;': '>', 
    '&amp;': '&', 
    '&quot;': '"', 
    '&apos;': "'",
    '&#39;': "'", 
    '&#34;': '"',
    '&#38;': '&',
    '&#60;': '<',
    '&#62;': '>',
    '&#160;': ' ',
    
    // Currency symbols
    '&cent;': '¢', 
    '&pound;': '£', 
    '&yen;': '¥', 
    '&euro;': '€',
    '&#162;': '¢',
    '&#163;': '£',
    '&#165;': '¥',
    '&#8364;': '€',
    
    // Special symbols
    '&copy;': '©', 
    '&reg;': '®', 
    '&trade;': '™',
    '&#169;': '©',
    '&#174;': '®',
    '&#8482;': '™',
    
    // Common accented characters
    '&aacute;': 'á',
    '&eacute;:': 'é',
    '&iacute;': 'í',
    '&oacute;': 'ó',
    '&uacute;': 'ú',
    '&auml;': 'ä',
    '&euml;': 'ë',
    '&iuml;': 'ï',
    '&ouml;': 'ö',
    '&uuml;': 'ü',
    '&ntilde;': 'ñ',
  };

  // First try the fast method with known entities
  let decoded = text;
  Object.entries(entities).forEach(([entity, char]) => {
    // Use a global replace with regex to replace all occurrences
    decoded = decoded.replace(new RegExp(entity, 'g'), char);
  });

  // Then handle numeric HTML entities that aren't in our map (&#NNNN;)
  decoded = decoded.replace(/&#(\d+);/g, (_, numStr) => {
    const num = parseInt(numStr, 10);
    return String.fromCodePoint(num);
  });

  // Handle hexadecimal entities (&#xNNNN;)
  decoded = decoded.replace(/&#x([0-9a-f]+);/gi, (_, hexStr) => {
    const num = parseInt(hexStr, 16);
    return String.fromCodePoint(num);
  });

  return decoded;
};

// Add this helper function at the top with other utility functions
const truncateText = (text, maxLength = 20) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// First update the truncateEmail helper function
const truncateEmail = (email) => {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  
  if (window.innerWidth <= 600) { // Mobile specific truncation
    return local.length > 6 ? `${local.slice(0, 6)}...@${domain}` : email;
  }
  return email; // Show full email on desktop
};

// Add this improved function to format complex answers for display
const formatDisplayAnswer = (answer, type) => {
  if (!answer || answer === 'undefined' || answer === '{}') return 'No response';
  
  try {
    if (type === 'image-choice') {
      const parsedAnswer = typeof answer === 'string' ? JSON.parse(answer) : answer;
      return parsedAnswer; // Return the full parsed object for image-choice
    }
    // First decode HTML entities if it's a string
    let decodedAnswer = typeof answer === 'string' ? decodeHtmlEntities(answer) : answer;
    
    // Try to parse JSON only if it looks like JSON
    let parsedAnswer = decodedAnswer;
    if (typeof decodedAnswer === 'string') {
      const trimmed = decodedAnswer.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
          (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          parsedAnswer = JSON.parse(decodedAnswer);
        } catch (e) {
          // If parsing fails, keep the decoded string
          parsedAnswer = decodedAnswer;
        }
      }
    }
    
    // Handle different question types
    switch(type?.toLowerCase()) {
      case 'matrix':
        if (typeof parsedAnswer === 'object' && parsedAnswer !== null) {
          return Object.entries(parsedAnswer)
            .map(([key, value]) => `Q${parseInt(key) + 1}: ${value}/5`)
            .join(', ');
        }
        return decodeHtmlEntities(String(decodedAnswer));
      
      case 'address':
        if (typeof parsedAnswer === 'object' && parsedAnswer !== null) {
          // Format address components in a more readable way
          const addressParts = [];
          if (parsedAnswer.street) addressParts.push(decodeHtmlEntities(parsedAnswer.street));
          if (parsedAnswer.city) addressParts.push(decodeHtmlEntities(parsedAnswer.city));
          if (parsedAnswer.state) addressParts.push(decodeHtmlEntities(parsedAnswer.state));
          if (parsedAnswer.zip) addressParts.push(decodeHtmlEntities(parsedAnswer.zip));
          if (parsedAnswer.country) addressParts.push(decodeHtmlEntities(parsedAnswer.country));
          
          return addressParts.join(', ');
        }
        return decodeHtmlEntities(String(decodedAnswer));
      
      case 'ranking':
      case 'multiple-choice':
      case 'checkboxes':
        if (Array.isArray(parsedAnswer)) {
          return parsedAnswer.map(item => 
            typeof item === 'string' ? decodeHtmlEntities(item) : String(item)
          ).join(', ');
        } else if (typeof parsedAnswer === 'object' && parsedAnswer !== null) {
          // Handle nested arrays in objects
          const values = Object.values(parsedAnswer).flat();
          return Array.isArray(values) ? 
            values.map(item => typeof item === 'string' ? decodeHtmlEntities(item) : String(item)).join(', ') : 
            decodeHtmlEntities(String(decodedAnswer));
        }
        return decodeHtmlEntities(String(decodedAnswer));
      
      default:
        // If it's already parsed into an object, stringify it nicely
        if (typeof parsedAnswer === 'object' && parsedAnswer !== null) {
          const processedObj = {};
          
          // Process each key-value pair to decode HTML entities
          Object.entries(parsedAnswer).forEach(([key, value]) => {
            const decodedKey = decodeHtmlEntities(key);
            let decodedValue = value;
            
            if (typeof value === 'string') {
              decodedValue = decodeHtmlEntities(value);
            } else if (Array.isArray(value)) {
              decodedValue = value.map(item => 
                typeof item === 'string' ? decodeHtmlEntities(item) : item
              );
            }
            
            processedObj[decodedKey] = decodedValue;
          });
          
          return Object.entries(processedObj)
            .map(([key, value]) => {
              const displayValue = Array.isArray(value) ? value.join(', ') : value;
              return `${key}: ${displayValue}`;
            })
            .join(', ');
        }
        return decodeHtmlEntities(String(decodedAnswer));
    }
  } catch (error) {
    // If any processing fails, at least decode the HTML entities in the original answer
    return decodeHtmlEntities(String(answer));
  }
};

// Also update the getQuestionText function used in the component
const getQuestionText = (questionText) => {
  return decodeHtmlEntities(questionText || '');
};

const Summary = () => {
  // Replace local state with Redux state using useSelector
  const dispatch = useDispatch();
  const {
    currentSurveyId: surveyId,
    analytics,
    questions,
    loading: { responses: loading },
    error: { responses: error }
  } = useSelector(state => state.surveyResponses);

  // Local UI state that doesn't need to be in Redux
  const [chartTypes, setChartTypes] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [chartEnabled, setChartEnabled] = useState({});

  // First add this state to track expanded answers
  const [expandedAnswers, setExpandedAnswers] = useState({});

  // Data is already fetched by parent component SurveyResponses.js
  // useEffect(() => {
  //   if (surveyId) {
  //     dispatch(fetchSurveyResponses(surveyId));
  //   }
  // }, [dispatch, surveyId]);

  useEffect(() => {
    if (analytics?.questions_analytics) {
      // Initialize expanded state for all questions
      const initialExpanded = analytics.questions_analytics.reduce((acc, question) => {
        acc[question.question_id] = false;
        return acc;
      }, {});
      setExpandedQuestions(initialExpanded);
    }
  }, [analytics]);

  const handleRetry = () => {
    dispatch(fetchSurveyResponses(surveyId));
  };

  const getQuestionIcon = (type) => {
    if (!type) return <TextFields sx={{ color: '#11998e' }} />; // Default icon
  
    switch (type.toLowerCase()) {
         case 'dropdown':
              return <ArrowDropDownIcon sx={{ color: '#11998e', fontSize: 20 }} />;
              case 'image-choice':
                      return <ImageIcon sx={{ color: '#11998e', fontSize: 20 }} />;
      case 'short-answer':
        return <TextFields sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'long-answer':
        return <Subject sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'multiple-choice':
        return <RadioButtonChecked sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'checkboxes':
        return <CheckBox sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'email':
        return <Email sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'number':
        return <Numbers sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'date':
        return <CalendarMonth sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'file-upload':
        return <Upload sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'rating':
        return <Star sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'phone':
        return <Phone sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'matrix':
        return <TableChart sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'likert-scale':
        return <CompareArrows sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'nps':
        return <Looks3 sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'ranking':
        return <Sort sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'datetime':
        return <EventNote sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'time':
        return <AccessTime sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'website':
        return <Language sx={{ color: '#11998e', fontSize: 28 }} />;
      case 'address':
        return <LocationOn sx={{ color: '#11998e', fontSize: 28 }} />;
      default:
        return <TextFields sx={{ color: '#11998e', fontSize: 28 }} />;
    }
  };

  // Update this function to better transform answers from raw data
  const transformAnswers = (question) => {
    if (!question.answers) return [];
    
    const questionData = questions.find(q => q.id === question.question_id);
    const questionType = questionData?.question_type;
    
    return Object.entries(question.answers).map(([answer, count]) => {
      // Format the answer for display
      const formattedAnswer = formatDisplayAnswer(answer, questionType);
      
      return {
        rawAnswer: answer, // Keep the raw answer for reference
        answer: formattedAnswer,
        count,
        percentage: Math.round((count / analytics.total_responses) * 100)
      };
    });
  };

  const renderSkeleton = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          boxShadow: '0 4px 20px rgba(17, 153, 142, 0.2)',
          mb: 3,
          p: 2
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton 
                variant="circular" 
                width={40} 
                height={40} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} 
              />
              <Box>
                <Skeleton 
                  variant="text" 
                  width={140} 
                  height={24} 
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} 
                />
                <Skeleton 
                  variant="text" 
                  width={100} 
                  height={20} 
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} 
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Skeleton 
                variant="text" 
                width={60} 
                height={48} 
                sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} 
              />
              <Skeleton 
                variant="rectangular" 
                width={140} 
                height={36} 
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 1
                }} 
              />
            </Box>
          </Box>
        </Card>
      </Grid>
  
      {[1, 2].map((item) => (
        <Grid item xs={12} key={item}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 2
          }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5,
                  mr: 2,
                  bgcolor: 'rgba(17, 153, 142, 0.05)',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Skeleton 
                    variant="circular" 
                    width={28} 
                    height={28} 
                    sx={{ bgcolor: 'rgba(17, 153, 142, 0.2)' }} 
                  />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Skeleton 
                    variant="text" 
                    width="40%" 
                    height={28} 
                    sx={{ mb: 1 }} 
                  />
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box>
                      <Skeleton variant="text" width={80} height={20} />
                      <Skeleton variant="text" width={40} height={24} />
                    </Box>
                    <Box>
                      <Skeleton variant="text" width={80} height={20} />
                      <Skeleton variant="text" width={40} height={24} />
                    </Box>
                  </Box>
                </Box>
  
                <Skeleton 
                  variant="rectangular" 
                  width={100} 
                  height={32} 
                  sx={{ 
                    borderRadius: 1,
                    bgcolor: 'rgba(17, 153, 142, 0.1)'
                  }} 
                />
              </Box>
  
              <Box sx={{ mt: 2 }}>
                {[1, 2, 3].map((row) => (
                  <Box 
                    key={row} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: row < 3 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none'
                    }}
                  >
                    <Skeleton variant="text" width="30%" height={24} />
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Skeleton variant="text" width={60} height={24} />
                      <Skeleton 
                        variant="rectangular" 
                        width={50} 
                        height={28} 
                        sx={{ borderRadius: 1 }} 
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const handleChartTypeChange = (questionId, newValue) => {
    setChartTypes(prev => ({
      ...prev,
      [questionId]: newValue
    }));
  };

  const shouldShowChart = (type) => {
    return ['multiple-choice', 'checkboxes','dropdown'].includes(type);
  };

  const getDefaultChartType = (type) => {
    switch (type) {
      case 'multiple-choice':
      case 'checkboxes':
      case 'dropdown':
        return 'pie';
      default:
        return 'list';
    }
  };

  useEffect(() => {
    if (questions.length > 0) {
      const initialChartTypes = questions.reduce((acc, question) => {
        if (shouldShowChart(question.question_type)) {
          acc[question.id] = getDefaultChartType(question.question_type);
        }
        return acc;
      }, {});
      setChartTypes(initialChartTypes);
    }
  }, [questions]);

  const pieChartConfig = {
    margin: { 
      top: 20,
      right: 20,
      bottom: 80,
      left: 20
    },
    innerRadius: 0.5,
    padAngle: 0.7,
    cornerRadius: 3,
    activeOuterRadiusOffset: 8,
    colors: { scheme: 'set3' },
    borderWidth: 1,
    borderColor: { from: 'color', modifiers: [['darker', 0.2]] },
    arcLinkLabelsSkipAngle: 10,
    arcLinkLabelsTextColor: "#333333",
    arcLinkLabelsThickness: 2,
    arcLinkLabelsColor: { from: 'color' },
    arcLabelsSkipAngle: 10,
    arcLabelsTextColor: { from: 'color', modifiers: [['darker', 2]] },
    legends: [
      // {
      //   anchor: 'bottom',
      //   direction: 'column',
      //   justify: false,
      //   translateY: 50,
      //   itemsSpacing: 5,
      //   itemWidth: 100,
      //   itemHeight: 20,
      //   itemDirection: 'left-to-right',
      //   itemOpacity: 1,
      //   symbolSize: 12,
      //   symbolShape: 'circle'
      // }
    ]
  };

  const handleSwitchChange = (questionId) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const isChecked = event.target.checked;
    
    setChartEnabled(prev => ({
      ...prev,
      [questionId]: isChecked
    }));

    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: isChecked
    }));

    if (isChecked) {
      setChartTypes(prev => ({
        ...prev,
        [questionId]: 'pie'
      }));
    }
  };

  const handleAccordionChange = (questionId) => (event, expanded) => {
    if (!chartEnabled[questionId]) {
      setExpandedQuestions(prev => ({
        ...prev,
        [questionId]: expanded
      }));
    }
  };

  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF();
      let yPos = 15;
  
      const pageWidth = doc.internal.pageSize.width;
  
      // Load the SurveySlack logo and name close together
      const logoWidth = 12;
      const logoHeight = 12;
      const logoX = pageWidth - 60;
      const textX = logoX + logoWidth + 2;
  
      // Add SurveySlack logo (use the compact version here)
      doc.addImage(logo, 'PNG', logoX, yPos - 3, logoWidth, logoHeight);
  
      // Add SurveySlack text
      doc.setFontSize(14);
      doc.setTextColor(17, 153, 142); // Teal
      doc.text('SurveySlack', textX, yPos + 6);
  
      // Reset text color
      doc.setTextColor(0, 0, 0);
  
      // Helper function to format complex answers
      const formatPDFAnswer = (answer, type) => {
        if (!answer || answer === 'undefined' || answer === '{}') return 'Not answered';
        
        try {
          if (type === 'image-choice') {
            let parsedAnswer = typeof answer === 'string' ? JSON.parse(answer) : answer;
            
            // Handle array format (format 2)
  if (Array.isArray(parsedAnswer)) {
    return `Image: ${parsedAnswer[0]}`; // Return full URL
  }
            
            // Handle object format (format 1)
            let displayParts = [];
            
           // Add full URL
  if (parsedAnswer.imageUrl) {
    displayParts.push(`Image: ${parsedAnswer.imageUrl}`);
  }
            
            // Add label if present
            if (parsedAnswer.label) {
              displayParts.push(`Label: ${parsedAnswer.label}`);
            }
            
            // Add description if present and showDescription is true
            if (parsedAnswer.showDescription && parsedAnswer.description) {
              displayParts.push(`Description: ${parsedAnswer.description}`);
            }
            
            return displayParts.join(' | ');
          }
          switch(type) {
            
            case 'matrix':
              const matrixData = JSON.parse(answer);
              return Object.entries(matrixData)
                .map(([key, value]) => `Q${parseInt(key) + 1}: ${value}/5`)
                .join(', ');
            
            case 'address':
              const addr = JSON.parse(answer);
              return Object.entries(addr)
                .filter(([_, value]) => value && value !== 'undefined')
                .map(([_, value]) => value)
                .join(', ');
            
            case 'ranking':
            case 'multiple':
            case 'checkboxes':
              const parsed = JSON.parse(answer);
              return Array.isArray(parsed) ? parsed.join(', ') : 
                     parsed?.undefined ? parsed.undefined.join(' > ') : answer;
            
            case 'datetime':
            case 'date':
              const date = new Date(answer);
              return date instanceof Date && !isNaN(date) 
                ? format(date, 'MMM d, yyyy h:mm a')
                : answer;
            
            default:
              return String(answer);
          }
        } catch (error) {
          return String(answer);
        }
      };


  
      // Add title
      doc.setFontSize(20);
const surveyTitle = analytics?.survey_name;
const maxWidth = pageWidth - 100; // Leave 15px margin on each side
const splitTitle = doc.splitTextToSize(surveyTitle, maxWidth);

// Add some spacing after the logo
yPos += 10;
splitTitle.forEach(line => {
  doc.text(line, 15, yPos);
  yPos += 10; // Increase spacing between lines
});

// Add extra spacing after the title
yPos += 5;
// console.log(`Survey Title: ${surveyTitle}`); // Debugging line

  
      // Add generation date
      doc.setFontSize(12);
      doc.text(`Generated on: ${format(new Date(), 'MMM d, yyyy h:mm a')}`, 15, yPos);
      yPos += 20;
  
      // Horizontal line
      doc.setDrawColor(17, 153, 142);
      doc.line(15, yPos, pageWidth - 15, yPos);
      yPos += 10;
  
      // Overview Section
      doc.setFontSize(16);
      doc.text('Overview', 15, yPos);
      yPos += 10;
  
      const overviewData = [
        ['Total Responses', analytics.total_responses],
        ['Today\'s Responses', analytics.today_responses],
      ];
  
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: overviewData,
        theme: 'grid',
        headStyles: { fillColor: [17, 153, 142] },
        margin: { left: 15, right: 15 }
      });
  
      yPos = doc.lastAutoTable.finalY + 20;
  
      // Questions Section
      analytics.questions_analytics?.forEach((question, index) => {
        const answersArray = transformAnswers(question);
        const questionData = questions.find(q => q.id === question.question_id);

        if (yPos > doc.internal.pageSize.height - 60) {
          doc.addPage();
          yPos = 20;
        }

        // UPDATED PART: Fix question text wrapping
        doc.setFontSize(14);
        doc.setTextColor(17, 153, 142);
        
        // Get question text and decode it
        const questionText = questionData?.question_text ? 
          decodeHtmlEntities(questionData.question_text) : 'Untitled Question';
        
        // Format the question title with number
        const titleText = `Question ${index + 1}: ${questionText}`;
        
        // Split long text into multiple lines
        const splitTitle = doc.splitTextToSize(titleText, 180);
        doc.text(splitTitle, 15, yPos);
        
        // Adjust position based on how many lines the text takes
        const titleLines = splitTitle.length;
        yPos += 7 * titleLines;

        // Add question type on the next line (keep this part as is)
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Type: ${questionData?.question_type || 'Unknown'}`, 15, yPos);
        yPos += 13;

        // Reset text color
        doc.setTextColor(0, 0, 0);

        const answerData = answersArray.map(item => [
          formatPDFAnswer(item.answer, questionData?.question_type),
          item.count,
          `${item.percentage}%`
        ]);
  
        autoTable(doc, {
          startY: yPos,
          head: [['Answer', 'Responses', 'Percentage']],
          body: answerData,
          theme: 'grid',
          headStyles: { fillColor: [17, 153, 142] },
        });
  
        yPos = doc.lastAutoTable.finalY + 15;
      });
  
      // Footer
      const footerText = 'https://surveyslack.com/ VISIT THIS SITE FOR CREATING PROFESSIONAL AND MULTIFUNCTIONAL SURVEYS';
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(footerText, 15, doc.internal.pageSize.height - 10);
    
      doc.save(`survey-summary-${surveyId}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  // Add this function near the other state management functions
const toggleAnswer = (answerId) => {
  setExpandedAnswers(prev => ({
    ...prev,
    [answerId]: !prev[answerId]
  }));
};

  return (
    <Box sx={{ 
      p: { xs: 0, sm: 3 },
      '& .MuiGrid-container': {
        m: { xs: 0, sm: -2 }
      }, '@media (max-width: 780px)': {
        maxWidth: '94% !important'
      },
    }}>
      {loading ? (
        renderSkeleton()
      ) : error ? (
        <Box display="flex" 
             flexDirection="column" 
             alignItems="center" 
             gap={2} 
             p={3}
        >
          <Typography color="error">{error}</Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={handleRetry}
          >
            Retry
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(17, 153, 142, 0.2)',
              mb: 3
            }}>
              <CardContent>
                <Box sx={{ 
                  display: { xs: 'none', sm: 'flex' }, 
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleOutline sx={{ fontSize: 32, mr: 1 }} />
                    <Box>
                      <Typography variant="h6">Total Responses</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {analytics.today_responses} responses today
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>
                      {analytics.total_responses}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={handleDownloadPDF}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)'
                        }
                      }}
                    >
                      Download PDF
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
  
          {analytics.questions_analytics
            ?.map(question => ({
              ...question,
              sortIndex: questions.findIndex(q => q.id === question.question_id)
            }))
            .sort((a, b) => a.sortIndex - b.sortIndex)
            .map((question) => {
              const answersArray = transformAnswers(question);
              const totalResponses = answersArray.reduce((sum, item) => sum + item.count, 0);
              const questionData = questions.find(q => q.id === question.question_id);
              const questionIndex = question.sortIndex + 1;  // Use the sorted index
  
              return (
                <Grid item xs={12} key={question.question_id}>
                  <Accordion 
                    expanded={expandedQuestions[question.question_id] ?? false}
                    onChange={handleAccordionChange(question.question_id)}
                    sx={{ 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      borderRadius: '8px !important',
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: 2,
                          mr: 2,
                          bgcolor: 'rgba(17, 153, 142, 0.05)',
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          borderRadius: 1
                        }}>
                          {questionData && getQuestionIcon(questionData.question_type)}
                        </Box>
                        
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: '#11998e', 
                              mb: 1,
                              fontSize: { xs: '1rem', sm: '1.25rem' }, 
                            }}
                          >
                            Question {questionIndex}: {questionData ? getQuestionText(questionData.question_text) : 'Untitled Question'}
                          </Typography>
                          
                          <Stack direction="row" spacing={3} alignItems="center">
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Total Responses
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#11998e' }}>
                                {totalResponses}
                              </Typography>
                            </Box>
                            
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Response Rate
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#11998e' }}>
                                {Math.round((totalResponses / analytics.total_responses) * 100)}%
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
  
                        {shouldShowChart(questionData?.question_type) && (
                          <Box sx={{ 
                            position: { xs: 'static', sm: 'absolute' },
                            right: { xs: 'auto', sm: 48 },
                            top: { xs: 'auto', sm: '50%' },
                            transform: { xs: 'none', sm: 'translateY(-50%)' },
                            zIndex: 2,
                            mt: { xs: 1, sm: 0 }, 
                            display: 'flex',
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                            alignItems: 'center',
                            pl: { xs: 2, sm: 0 } 
                          }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={!!chartEnabled[question.question_id]}
                                  onChange={handleSwitchChange(question.question_id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label={
                                <Typography 
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    color: 'text.secondary',
                                    ml: -0.5,
                                    display: { xs: 'none', sm: 'block' } 
                                  }}
                                >
                                  Show Chart
                                </Typography>
                              }
                              sx={{
                                margin: 0,
                                '& .MuiSwitch-root': {
                                  width: 38,
                                  height: 22,
                                  mr: { xs: 0, sm: 1 } 
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </AccordionSummary>
    
                    <AccordionDetails>
                      {chartEnabled[question.question_id] ? (
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ mb: 3 }}>
                            <ToggleButtonGroup
                              value={chartTypes[question.question_id]}
                              exclusive
                              onChange={(e, newValue) => handleChartTypeChange(question.question_id, newValue || 'pie')}
                              aria-label="visualization type"
                              size="small"
                            >
                              <ToggleButton value="pie" aria-label="pie chart">
                                <PieChart sx={{ color: '#11998e' }} />
                              </ToggleButton>
                              <ToggleButton value="list" aria-label="list view">
                                <ViewList sx={{ color: '#11998e' }} />
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                          
                          {chartTypes[question.question_id] === 'pie' ? (
                            <Box sx={{ height: 350, width: '100%', mt: 2 }}>
                              <ResponsivePie
                                data={answersArray.map(item => ({
                                  id: item.answer || 'No Answer',
                                  label: truncateText(item.answer || 'No Answer', 30),
                                  value: item.count,
                                  rawValue: item.rawAnswer // Keep raw value for reference
                                }))}
                                {...pieChartConfig}
                                enableArcLabels={true}
                                enableArcLinkLabels={false}
                                arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                                arcLinkLabelsColor={{ from: 'color', modifiers: [['darker', 2]] }}
                                isInteractive={true}
                                motionConfig="gentle"
                              />
                            </Box>
                          ) : (
                            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                              {answersArray.map((item, answerIndex) => (
                                <React.Fragment key={answerIndex}>
                                  <ListItem
                                    sx={{
                                      py: 2,
                                      position: 'relative',
                                      '&:hover': { bgcolor: 'rgba(17, 153, 142, 0.02)' }
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: `${item.percentage}%`,
                                        bgcolor: 'rgba(17, 153, 142, 0.1)',
                                        zIndex: 0
                                      }}
                                    />
                                    <ListItemText
                                      sx={{ zIndex: 1 }}
                                      primary={
                                        <Box sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: { xs: 'flex-start', sm: 'center' },
                                          flexDirection: { xs: 'column', sm: 'row' },
                                          gap: { xs: 1, sm: 0 }
                                        }}>
                                          <Box sx={{ 
                                            maxWidth: '100%',
                                            width: '100%'
                                          }}>
                                            {questionData?.question_type === 'image-choice' ? (
                                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
                                                <Box
                                                  component="img"
                                                  src={item.answer.imageUrl}
                                                  alt={item.answer.label || 'Selected image'}
                                                  sx={{
                                                    width: 100,
                                                    height: 100,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider'
                                                  }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                  {item.answer.label && (
                                                    <Typography 
                                                      variant="body1" 
                                                      sx={{ 
                                                        display: {
                                                          xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'block' : '-webkit-box',
                                                          sm: 'block'
                                                        },
                                                        WebkitLineClamp: {
                                                          xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'unset' : 3,
                                                          sm: 'unset'
                                                        },
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        mb: 0.5,
                                                        fontWeight: 500,
                                                        color: 'text.primary'
                                                      }}
                                                    >
                                                      <Box component="span" sx={{ color: 'text.secondary' }}>Label: </Box>
                                                      {item.answer.label}
                                                    </Typography>
                                                  )}
                                                  {item.answer.showDescription && item.answer.description && (
                                                    <Typography 
                                                      variant="body2" 
                                                      sx={{ 
                                                        display: {
                                                          xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'block' : '-webkit-box',
                                                          sm: 'block'
                                                        },
                                                        WebkitLineClamp: {
                                                          xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'unset' : 3,
                                                          sm: 'unset'
                                                        },
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        color: 'text.primary',
                                                        whiteSpace: 'pre-wrap'
                                                      }}
                                                    >
                                                      <Box component="span" sx={{ color: 'text.secondary' }}>Description: </Box>
                                                      {item.answer.description}
                                                    </Typography>
                                                  )}
                                                  {/* Add Read More button on mobile */}
                                                  {!questionData?.question_type !== 'image-choice' && item.answer?.length > 150 && (
  <Button
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      toggleAnswer(`${question.question_id}-${answerIndex}`);
    }}
    sx={{ 
      display: { xs: 'flex', sm: 'none' },
      mt: 1,
      color: '#11998e',
      '&:hover': {
        backgroundColor: 'rgba(17, 153, 142, 0.08)'
      }
    }}
  >
    {expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'Show Less' : 'Read More'}
  </Button>
)}
                                                </Box>
                                              </Box>
                                            ) : (
                                              <Box sx={{ width: '100%' }}>
  <Typography 
    variant="body1" 
    sx={{
      display: {
        xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'block' : '-webkit-box',
        sm: 'block'
      },
      WebkitLineClamp: {
        xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'unset' : 3,
        sm: 'unset'
      },
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      wordBreak: 'break-word'
    }}
  >
    {item.answer}
  </Typography>
  
  {/* Add Read More button - Fix the condition */}
  {typeof item.answer === 'string' && item.answer.length > 150 && (
    <Button
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        toggleAnswer(`${question.question_id}-${answerIndex}`);
      }}
      sx={{ 
        display: { xs: 'flex', sm: 'none' },
        mt: 1,
        color: '#11998e',
        '&:hover': {
          backgroundColor: 'rgba(17, 153, 142, 0.08)'
        }
      }}
    >
      {expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'Show Less' : 'Read More'}
    </Button>
  )}
</Box>
                                            )}
                                          </Box>
                                          
                                          <Stack 
                                            direction="row" 
                                            spacing={2} 
                                            alignItems="center"
                                            sx={{
                                              width: { xs: '100%', sm: 'auto' },
                                              justifyContent: { xs: 'space-between', sm: 'flex-end' },
                                              flexShrink: 0  
                                            }}
                                          >
                                            <Typography 
                                              variant="body2" 
                                              color="text.secondary"
                                              sx={{ 
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                whiteSpace: 'nowrap'  
                                              }}
                                            >
                                              {item.count} {item.count === 1 ? 'response' : 'responses'}
                                            </Typography>
                                            <Box sx={{
                                              color: '#11998e',
                                              bgcolor: 'rgba(17, 153, 142, 0.1)',
                                              px: 1.5,
                                              py: 0.5,
                                              borderRadius: 1,
                                              minWidth: 45,
                                              textAlign: 'center',
                                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                              whiteSpace: 'nowrap'  
                                            }}>
                                              {item.percentage}%
                                            </Box>
                                          </Stack>
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                  {answerIndex < answersArray.length - 1 && <Divider />}
                                </React.Fragment>
                              ))}
                            </List>
                          )}
                        </Box>
                      ) : (
                        <List>
                          {answersArray.map((item, answerIndex) => (
                            <React.Fragment key={answerIndex}>
                              <ListItem
                                sx={{
                                  py: 2,
                                  position: 'relative',
                                  '&:hover': { bgcolor: 'rgba(17, 153, 142, 0.02)' }
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: { xs: 'flex-start', sm: 'center' },
                                      flexDirection: { xs: 'column', sm: 'row' },
                                      gap: { xs: 1, sm: 0 }
                                    }}>
                                      <Box sx={{ 
                                        maxWidth: '100%',
                                        overflow: { xs: 'auto', sm: 'hidden' },   
                                        paddingBottom: { xs: 1, sm: 0 },         
                                        width: '100%'
                                      }}>
                                        {questionData?.question_type === 'image-choice' ? (
                                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
                                            <Box
                                              component="img"
                                              src={item.answer.imageUrl}
                                              alt={item.answer.label || 'Selected image'}
                                              sx={{
                                                width: 100,
                                                height: 100,
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'divider'
                                              }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                              {item.answer.label && (
                                                <Typography 
                                                  variant="body1" 
                                                  sx={{ 
                                                    display: {
                                                      xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'block' : '-webkit-box',
                                                      sm: 'block'
                                                    },
                                                    WebkitLineClamp: {
                                                      xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'unset' : 3,
                                                      sm: 'unset'
                                                    },
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    mb: 0.5,
                                                    fontWeight: 500,
                                                    color: 'text.primary'
                                                  }}
                                                >
                                                  <Box component="span" sx={{ color: 'text.secondary' }}>Label: </Box>
                                                  {item.answer.label}
                                                </Typography>
                                              )}
                                              {item.answer.showDescription && item.answer.description && (
                                                <Typography 
                                                  variant="body2" 
                                                  sx={{ 
                                                    display: {
                                                      xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'block' : '-webkit-box',
                                                      sm: 'block'
                                                    },
                                                    WebkitLineClamp: {
                                                      xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'unset' : 3,
                                                      sm: 'unset'
                                                    },
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    color: 'text.primary',
                                                    whiteSpace: 'pre-wrap'
                                                  }}
                                                >
                                                  <Box component="span" sx={{ color: 'text.secondary' }}>Description: </Box>
                                                  {item.answer.description}
                                                </Typography>
                                              )}
                                            </Box>
                                          </Box>
                                        ) : (
                                          <Box sx={{ width: '100%' }}>
  <Typography 
    variant="body1" 
    sx={{
      display: {
        xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'block' : '-webkit-box',
        sm: 'block'
      },
      WebkitLineClamp: {
        xs: expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'unset' : 3,
        sm: 'unset'
      },
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      wordBreak: 'break-word'
    }}
  >
    {item.answer}
  </Typography>
  
  {/* Add Read More button - Fix the condition */}
  {typeof item.answer === 'string' && item.answer.length > 150 && (
    <Button
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        toggleAnswer(`${question.question_id}-${answerIndex}`);
      }}
      sx={{ 
        display: { xs: 'flex', sm: 'none' },
        mt: 1,
        color: '#11998e',
        '&:hover': {
          backgroundColor: 'rgba(17, 153, 142, 0.08)'
        }
      }}
    >
      {expandedAnswers[`${question.question_id}-${answerIndex}`] ? 'Show Less' : 'Read More'}
    </Button>
  )}
</Box>
                                        )}
                                      </Box>
                                      
                                      <Stack 
                                        direction="row" 
                                        spacing={2} 
                                        alignItems="center"
                                        sx={{
                                          width: { xs: '100%', sm: 'auto' },
                                          justifyContent: { xs: 'space-between', sm: 'flex-end' },
                                          flexShrink: 0  
                                        }}
                                      >
                                        <Typography 
                                          variant="body2" 
                                          color="text.secondary"
                                          sx={{ 
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            whiteSpace: 'nowrap'  
                                          }}
                                        >
                                          {item.count} {item.count === 1 ? 'response' : 'responses'}
                                        </Typography>
                                        <Box sx={{
                                          color: '#11998e',
                                          bgcolor: 'rgba(17, 153, 142, 0.1)',
                                          px: 1.5,
                                          py: 0.5,
                                          borderRadius: 1,
                                          minWidth: 45,
                                          textAlign: 'center',
                                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                          whiteSpace: 'nowrap'  
                                        }}>
                                          {item.percentage}%
                                        </Box>
                                      </Stack>
                                    </Box>
                                  }
                                />
                              </ListItem>
                              {answerIndex < answersArray.length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            })}
        </Grid>
      )}
    </Box>
  );
};

export default Summary;